import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { fetchClinics, fetchClinicsByCompany, Clinic, ClinicSimple } from '@/services/clinic-service';
import { Company, fetchCompany } from '@/services/company-service';
import { useAuth } from '@/contexts/AuthContext';

interface ClinicContextType {
  clinics: ClinicSimple[];
  company: Company | null;
  selectedClinic: ClinicSimple | null;
  selectedClinicId: string | undefined;
  setSelectedClinicId: (id: string) => void;
  isLoadingClinics: boolean;
}

const ClinicContext = createContext<ClinicContextType | undefined>(undefined);

export function ClinicProvider({ children }: { children: ReactNode }) {
  const { accessToken, user, isAdmin } = useAuth();  // ← observa o token e o usuário

  const [clinics, setClinics] = useState<ClinicSimple[]>([]);
  const [company, setCompany] = useState<Company | null>(null);
  const [selectedClinicId, setSelectedClinicIdState] = useState<string | undefined>(undefined);
  const [isLoadingClinics, setIsLoadingClinics] = useState(false);

  useEffect(() => {
    // Reseta tudo quando o token mudar (troca de usuário ou logout)
    setClinics([]);
    setCompany(null);
    setSelectedClinicIdState(undefined);

    if (!accessToken) return;  // deslogado — não busca nada

    // Admin não busca clínicas (não tem company_id)
    if (isAdmin) {
      setIsLoadingClinics(false);
      return;
    }

    // Não admin: busca clínicas da empresa
    if (user?.company_id) {
      setIsLoadingClinics(true);

      // Busca clínicas por empresa
      fetchClinicsByCompany(user.company_id)
        .then((clinicsData) => {
          const allowed = user.clinic_ids ?? [];
          const scoped = allowed.length > 0
            ? clinicsData.filter((c) => allowed.includes(c.id))
            : clinicsData;
          setClinics(scoped);
          if (scoped.length === 1) {
            setSelectedClinicIdState(scoped[0].id);
          }
        })
        .catch(console.error)
        .finally(() => setIsLoadingClinics(false));

      // Busca empresa do usuário logado
      fetchCompany(user.company_id)
        .then(setCompany)
        .catch(console.error);
    } else {
      setIsLoadingClinics(false);
    }
  }, [accessToken, user?.company_id, isAdmin]);  // ← re-executa ao trocar de usuário

  const setSelectedClinicId = (id: string) => setSelectedClinicIdState(id);

  const selectedClinic = clinics.find((c) => c.id === selectedClinicId) ?? null;

  return (
    <ClinicContext.Provider
      value={{ clinics, company, selectedClinic, selectedClinicId, setSelectedClinicId, isLoadingClinics }}
    >
      {children}
    </ClinicContext.Provider>
  );
}

export function useClinic() {
  const context = useContext(ClinicContext);
  if (!context) throw new Error('useClinic must be used within ClinicProvider');
  return context;
}